import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import SkeletonLoader from 'shared/components/ui/SkeletonLoader';
import AISummaryButton from 'shared/components/Credits/AISummaryButton';

// Modern outline SVG icons
const Icons = {
  AIMagicStar: ({ size = 20, className = '' }) => (
    <svg
      width={size}
      height={size}
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      className={className}
    >
      {/* Main central star */}
      <path
        d='M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z'
        fill='currentColor'
        stroke='currentColor'
        strokeWidth='0.5'
        strokeLinejoin='round'
      />

      {/* Top sparkle */}
      <path d='M17 4L17.5 5.5L19 6L17.5 6.5L17 8L16.5 6.5L15 6L16.5 5.5L17 4Z' fill='currentColor' />

      {/* Bottom right sparkle */}
      <path d='M20 16L20.3 16.7L21 17L20.3 17.3L20 18L19.7 17.3L19 17L19.7 16.7L20 16Z' fill='currentColor' />

      {/* Top left small sparkle */}
      <path d='M6 3L6.2 3.5L6.7 3.7L6.2 3.9L6 4.4L5.8 3.9L5.3 3.7L5.8 3.5L6 3Z' fill='currentColor' />

      {/* Bottom left small sparkle */}
      <path d='M5 20L5.2 20.5L5.7 20.7L5.2 20.9L5 21.4L4.8 20.9L4.3 20.7L4.8 20.5L5 20Z' fill='currentColor' />

      {/* Magic swirl accent */}
      <path d='M8.5 6.5C9 6 9.5 6.5 9 7C8.5 7.5 8 7 8.5 6.5Z' fill='currentColor' opacity='0.7' />
      <path d='M15.5 13.5C16 13 16.5 13.5 16 14C15.5 14.5 15 14 15.5 13.5Z' fill='currentColor' opacity='0.7' />
    </svg>
  ),

  Sparkles: ({ size = 20, className = '' }) => (
    <svg
      width={size}
      height={size}
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      className={className}
    >
      <path d='m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z' />
      <path d='M5 3v4' />
      <path d='M19 17v4' />
      <path d='M3 5h4' />
      <path d='M17 19h4' />
    </svg>
  ),

  Lightbulb: ({ size = 20, className = '' }) => (
    <svg
      width={size}
      height={size}
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      className={className}
    >
      <path d='M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5' />
      <path d='M9 18h6' />
      <path d='M10 22h4' />
    </svg>
  ),

  MessageSquare: ({ size = 20, className = '' }) => (
    <svg
      width={size}
      height={size}
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      className={className}
    >
      <path d='M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z' />
    </svg>
  ),

  Target: ({ size = 20, className = '' }) => (
    <svg
      width={size}
      height={size}
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      className={className}
    >
      <circle cx='12' cy='12' r='10' />
      <circle cx='12' cy='12' r='6' />
      <circle cx='12' cy='12' r='2' />
    </svg>
  ),

  Settings: ({ size = 20, className = '' }) => (
    <svg
      width={size}
      height={size}
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      className={className}
    >
      <path d='M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.38a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z' />
      <circle cx='12' cy='12' r='3' />
    </svg>
  ),

  Robot: ({ size = 20, className = '' }) => (
    <svg
      width={size}
      height={size}
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      className={className}
    >
      <path d='M12 8V4H8' />
      <rect width='16' height='12' x='4' y='8' rx='2' />
      <path d='M2 14h2' />
      <path d='M20 14h2' />
      <path d='M15 13v2' />
      <path d='M9 13v2' />
    </svg>
  ),

  AlertTriangle: ({ size = 16, className = '' }) => (
    <svg
      width={size}
      height={size}
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      className={className}
    >
      <path d='m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z' />
      <path d='M12 9v4' />
      <path d='M12 17h.01' />
    </svg>
  ),

  Check: ({ size = 16, className = '' }) => (
    <svg
      width={size}
      height={size}
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      className={className}
    >
      <polyline points='20,6 9,17 4,12' />
    </svg>
  ),

  ThumbsUp: ({ size = 16, className = '' }) => (
    <svg
      width={size}
      height={size}
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      className={className}
    >
      <path d='M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3' />
    </svg>
  ),

  ThumbsDown: ({ size = 16, className = '' }) => (
    <svg
      width={size}
      height={size}
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      className={className}
    >
      <path d='M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17' />
    </svg>
  ),

  ChevronDown: ({ size = 16, className = '' }) => (
    <svg
      width={size}
      height={size}
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      className={className}
    >
      <polyline points='6,9 12,15 18,9' />
    </svg>
  ),

  Star: ({ size = 16, className = '', filled = false }) => (
    <svg
      width={size}
      height={size}
      viewBox='0 0 24 24'
      fill={filled ? 'currentColor' : 'none'}
      stroke='currentColor'
      strokeWidth='2'
      className={className}
    >
      <polygon points='12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26' />
    </svg>
  ),
};

const LikieeAISummary = ({
  aiSummary,
  reviewsAggregation,
  productName,
  isLoading = false,
  likieeId,
  showAIButton = false,
  onAIButtonSuccess,
}) => {
  const { t } = useTranslation();
  const [expandedSection, setExpandedSection] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Smart data availability checks - memoized for performance (moved before early returns)
  const hasReviewData = useMemo(() => reviewsAggregation?.totalReviews > 0, [reviewsAggregation?.totalReviews]);
  const hasAIInsights = useMemo(
    () => aiSummary?.highlights?.length > 0 || aiSummary?.keyFeatures?.length > 0 || (!hasReviewData && productName),
    [aiSummary?.highlights?.length, aiSummary?.keyFeatures?.length, hasReviewData, productName],
  );
  const hasConfidenceData = useMemo(() => aiSummary?.confidenceScore !== undefined, [aiSummary?.confidenceScore]);
  const hasRatingData = useMemo(() => reviewsAggregation?.averageRating > 0, [reviewsAggregation?.averageRating]);

  const toggleMainExpansion = useCallback(
    e => {
      setIsExpanded(prev => !prev);
      setExpandedSection(prevExpanded => (isExpanded ? null : prevExpanded));
    },
    [isExpanded],
  );

  const toggleSection = useCallback(section => {
    setExpandedSection(prevSection => (prevSection === section ? null : section));
  }, []);

  const renderStarRating = useCallback(rating => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<Icons.Star key={i} size={14} className='star filled' filled={true} />);
      } else if (i === fullStars && hasHalfStar) {
        stars.push(<Icons.Star key={i} size={14} className='star half' filled={true} />);
      } else {
        stars.push(<Icons.Star key={i} size={14} className='star empty' filled={false} />);
      }
    }
    return stars;
  }, []);

  const renderConfidenceIndicator = useCallback(score => {
    const percentage = Math.round(score * 100);
    let confidenceLevel = 'Low';
    let confidenceClass = 'low';

    if (percentage >= 80) {
      confidenceLevel = 'High';
      confidenceClass = 'high';
    } else if (percentage >= 60) {
      confidenceLevel = 'Medium';
      confidenceClass = 'medium';
    }

    return (
      <div className='confidence-indicator'>
        <div className={`confidence-badge ${confidenceClass}`}>
          <span className='confidence-text'>{confidenceLevel}</span>
          <span className='confidence-percentage'>{percentage}%</span>
        </div>
      </div>
    );
  }, []);

  // Generate fallback features based on product analysis - memoized for performance
  const generateFallbackFeatures = useMemo(() => {
    const features = [];

    if (productName) {
      const productNameLower = productName.toLowerCase();

      // Technology-based features
      if (productNameLower.includes('smart') || productNameLower.includes('ai')) {
        features.push(t('ai_feature_smart_technology', 'Smart technology integration'));
      }
      if (productNameLower.includes('wireless') || productNameLower.includes('bluetooth')) {
        features.push(t('ai_feature_wireless', 'Wireless connectivity'));
      }
      if (
        productNameLower.includes('fast') ||
        productNameLower.includes('quick') ||
        productNameLower.includes('rapid')
      ) {
        features.push(t('ai_feature_performance', 'High-performance operation'));
      }
      if (
        productNameLower.includes('portable') ||
        productNameLower.includes('compact') ||
        productNameLower.includes('mini')
      ) {
        features.push(t('ai_feature_portable', 'Compact and portable design'));
      }
      if (
        productNameLower.includes('pro') ||
        productNameLower.includes('premium') ||
        productNameLower.includes('advanced')
      ) {
        features.push(t('ai_feature_professional', 'Professional-grade features'));
      }
      if (
        productNameLower.includes('eco') ||
        productNameLower.includes('green') ||
        productNameLower.includes('sustainable')
      ) {
        features.push(t('ai_feature_eco_friendly', 'Environmentally conscious design'));
      }

      // Category-specific features
      const categoryFeatures = {
        'smartphone|phone|mobile': [
          t('ai_feature_mobile_connectivity', 'Mobile connectivity features'),
          t('ai_feature_camera_system', 'Advanced camera system'),
          t('ai_feature_battery_life', 'Optimized battery performance'),
        ],
        'laptop|computer|pc|macbook': [
          t('ai_feature_computing_power', 'High-performance computing'),
          t('ai_feature_display_quality', 'Quality display technology'),
          t('ai_feature_productivity', 'Productivity-focused design'),
        ],
        'headphones|earbuds|speakers|audio': [
          t('ai_feature_audio_quality', 'Superior audio quality'),
          t('ai_feature_noise_canceling', 'Noise management features'),
          t('ai_feature_comfort_design', 'Ergonomic comfort design'),
        ],
        'camera|photography|lens': [
          t('ai_feature_image_quality', 'Professional image quality'),
          t('ai_feature_optical_system', 'Advanced optical system'),
          t('ai_feature_creative_controls', 'Creative control options'),
        ],
        'gaming|game|console|controller': [
          t('ai_feature_gaming_performance', 'Optimized gaming performance'),
          t('ai_feature_responsive_controls', 'Responsive control system'),
          t('ai_feature_immersive_experience', 'Immersive gaming experience'),
        ],
        'watch|fitness|health|tracker': [
          t('ai_feature_health_monitoring', 'Health monitoring capabilities'),
          t('ai_feature_fitness_tracking', 'Comprehensive fitness tracking'),
          t('ai_feature_battery_efficiency', 'Efficient battery management'),
        ],
      };

      Object.entries(categoryFeatures).forEach(([keywords, categoryFeaturesArray]) => {
        const keywordArray = keywords.split('|');
        if (keywordArray.some(keyword => productNameLower.includes(keyword))) {
          features.push(...categoryFeaturesArray.slice(0, 2));
        }
      });
    }

    // AI analysis features (generic fallbacks)
    if (features.length < 3) {
      features.push(
        t('ai_feature_quality_analysis', 'Quality analysis available'),
        t('ai_feature_specification_review', 'Detailed specification review'),
        t('ai_feature_comparison_data', 'Comparison data insights'),
      );
    }

    return features.slice(0, 6);
  }, [productName, t]);

  // Generate universal AI insights that work for all products - memoized for performance
  const generateUniversalInsights = useMemo(() => {
    const insights = [];

    if (hasRatingData) {
      const rating = reviewsAggregation.averageRating;
      if (rating >= 4.5) {
        insights.push(t('ai_insight_highly_rated', 'Highly rated by customers'));
      } else if (rating >= 4.0) {
        insights.push(t('ai_insight_well_liked', 'Well-liked by most users'));
      } else if (rating >= 3.0) {
        insights.push(t('ai_insight_mixed_reviews', 'Mixed customer feedback'));
      }
    }

    if (hasReviewData) {
      const reviews = reviewsAggregation.totalReviews;
      if (reviews >= 1000) {
        insights.push(t('ai_insight_extensively_reviewed', 'Extensively reviewed product'));
      } else if (reviews >= 100) {
        insights.push(t('ai_insight_well_reviewed', 'Well-documented customer experience'));
      }
    }

    if (aiSummary?.primaryCategory) {
      insights.push(t('ai_insight_category_leader', `Popular in ${aiSummary.primaryCategory} category`));
    }

    if (aiSummary?.keyFeatures?.length >= 5) {
      insights.push(t('ai_insight_feature_rich', 'Feature-rich product'));
    }

    // Enhanced insights for products without reviews
    if (!hasReviewData) {
      insights.push(t('ai_insight_detailed_analysis', 'Comprehensive AI analysis available'));

      if (productName) {
        const productNameLower = productName.toLowerCase();

        // Enhanced brand recognition
        const brandKeywords = [
          'Apple',
          'Samsung',
          'Sony',
          'LG',
          'Bosch',
          'Philips',
          'Dyson',
          'Nike',
          'Adidas',
          'Microsoft',
          'Google',
          'Amazon',
          'HP',
          'Dell',
          'Lenovo',
          'ASUS',
          'Canon',
          'Nikon',
          'Logitech',
          'Razer',
          'Corsair',
          'JBL',
          'Bose',
          'Beats',
          'Sennheiser',
          'Fitbit',
          'GoPro',
          'Tesla',
          'BMW',
          'Mercedes',
          'Audi',
          'Intel',
          'AMD',
          'NVIDIA',
        ];
        const foundBrand = brandKeywords.find(brand => productNameLower.includes(brand.toLowerCase()));
        if (foundBrand) {
          insights.push(t('ai_insight_trusted_brand', `${foundBrand} brand quality`));
        }

        // Category-specific insights
        const categoryInsights = {
          'smartphone|phone|mobile': 'ai_insight_smartphone_features',
          'laptop|computer|pc|macbook': 'ai_insight_computing_power',
          'headphones|earbuds|speakers|audio': 'ai_insight_audio_quality',
          'camera|photography|lens': 'ai_insight_imaging_tech',
          'gaming|game|console|controller': 'ai_insight_gaming_performance',
          'watch|fitness|health|tracker': 'ai_insight_health_tracking',
          'home|smart|iot|alexa|google': 'ai_insight_smart_home',
          'kitchen|cooking|appliance': 'ai_insight_kitchen_efficiency',
          'clothing|fashion|apparel|shoes': 'ai_insight_style_quality',
          'tablet|ipad|surface': 'ai_insight_versatile_device',
          'tv|television|monitor|display': 'ai_insight_visual_experience',
          'car|automotive|vehicle': 'ai_insight_automotive_tech',
        };

        Object.entries(categoryInsights).forEach(([keywords, translationKey]) => {
          const keywordArray = keywords.split('|');
          if (keywordArray.some(keyword => productNameLower.includes(keyword))) {
            const fallbackText = translationKey.replace('ai_insight_', '').replace(/_/g, ' ');
            insights.push(t(translationKey, `Advanced ${fallbackText} features`));
          }
        });

        // Technology insights
        if (productNameLower.includes('wireless') || productNameLower.includes('bluetooth')) {
          insights.push(t('ai_insight_wireless_tech', 'Wireless connectivity features'));
        }
        if (productNameLower.includes('smart') || productNameLower.includes('ai')) {
          insights.push(t('ai_insight_smart_features', 'Smart technology integration'));
        }
      }

      // AI confidence-based insights
      if (hasConfidenceData) {
        const confidence = aiSummary.confidenceScore;
        if (confidence >= 0.8) {
          insights.push(t('ai_insight_high_confidence', 'High-confidence AI analysis'));
        } else if (confidence >= 0.6) {
          insights.push(t('ai_insight_good_analysis', 'Reliable product analysis'));
        }
      }

      if (insights.length < 3) {
        insights.push(t('ai_insight_new_product', 'Recently added to catalog'));
      }
    }

    return insights.length > 0 ? insights : [t('ai_insight_default', 'AI analysis available')];
  }, [
    hasRatingData,
    hasReviewData,
    hasConfidenceData,
    reviewsAggregation?.averageRating,
    reviewsAggregation?.totalReviews,
    aiSummary?.primaryCategory,
    aiSummary?.keyFeatures?.length,
    aiSummary?.confidenceScore,
    productName,
    t,
  ]);

  if (isLoading) {
    return (
      <div className='ai-summary-container'>
        <div className='ai-summary-card'>
          <SkeletonLoader type='basic' count={6} />
        </div>
      </div>
    );
  }

  if (!aiSummary && !reviewsAggregation) {
    return null;
  }

  const renderKeyFeatures = () => {
    if (aiSummary?.keyFeatures?.length) {
      return (
        <div className='ai-section key-features'>
          <h4 className='section-title'>
            <Icons.Sparkles size={20} className='section-icon' />
            {t('ai_summary_key_features', 'Key Features')}
          </h4>
          <div className='features-grid'>
            {aiSummary.keyFeatures.slice(0, 6).map((feature, index) => (
              <div key={index} className='feature-item'>
                <span className='feature-text'>{feature}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (!hasReviewData && productName) {
      const fallbackFeatures = generateFallbackFeatures;
      if (fallbackFeatures.length > 0) {
        return (
          <div className='ai-section key-features'>
            <h4 className='section-title'>
              <Icons.Sparkles size={20} className='section-icon' />
              {t('ai_summary_analysis_features', 'Analysis Features')}
            </h4>
            <div className='features-grid'>
              {fallbackFeatures.slice(0, 6).map((feature, index) => (
                <div key={index} className='feature-item'>
                  <span className='feature-text'>{feature}</span>
                </div>
              ))}
            </div>
          </div>
        );
      }
    }

    return null;
  };

  const renderHighlights = () => {
    if (aiSummary?.highlights?.length) {
      return (
        <div className='ai-section highlights'>
          <h4 className='section-title'>
            <Icons.Lightbulb size={20} className='section-icon' />
            {t('ai_summary_highlights', 'AI Highlights')}
          </h4>
          <ul className='highlights-list'>
            {aiSummary.highlights.slice(0, 4).map((highlight, index) => (
              <li key={index} className='highlight-item'>
                <Icons.Check size={16} className='highlight-icon' />
                {highlight}
              </li>
            ))}
          </ul>
        </div>
      );
    }

    if (!hasReviewData && productName) {
      const universalInsights = generateUniversalInsights;
      if (universalInsights.length > 0) {
        return (
          <div className='ai-section highlights'>
            <h4 className='section-title'>
              <Icons.Lightbulb size={20} className='section-icon' />
              {t('ai_summary_product_insights', 'Product Insights')}
            </h4>
            <ul className='highlights-list'>
              {universalInsights.slice(0, 4).map((insight, index) => (
                <li key={index} className='highlight-item'>
                  <Icons.Check size={16} className='highlight-icon' />
                  {insight}
                </li>
              ))}
            </ul>
          </div>
        );
      }
    }

    return null;
  };

  const renderReviewsInsights = () => {
    if (!reviewsAggregation) return null;

    const { summary, averageRating, totalReviews, sentimentScore } = reviewsAggregation;
    return (
      <div className='ai-section reviews-insights'>
        <h4 className='section-title'>
          <Icons.MessageSquare size={20} className='section-icon' />
          {t('ai_summary_reviews', 'Reviews Insights')}
          {totalReviews && <span className='reviews-count'>({totalReviews.toLocaleString()} reviews)</span>}
        </h4>

        {averageRating && (
          <div className='rating-summary'>
            <div className='rating-display'>
              <div className='stars'>{renderStarRating(averageRating)}</div>
              <span className='rating-value'>{averageRating.toFixed(1)}</span>
            </div>
            {sentimentScore && (
              <div className='sentiment-bar'>
                <div className='sentiment-positive' style={{ width: `${sentimentScore.positive}%` }} />
                <div className='sentiment-neutral' style={{ width: `${sentimentScore.neutral}%` }} />
                <div className='sentiment-negative' style={{ width: `${sentimentScore.negative}%` }} />
              </div>
            )}
          </div>
        )}

        {summary && (
          <div className='review-summary'>
            {summary.topPositives?.length > 0 && (
              <div className='summary-section positive'>
                <h5 className='summary-title'>
                  <Icons.ThumbsUp size={16} className='summary-icon' />
                  {t('ai_summary_what_users_love', 'What Users Love')}
                </h5>
                <ul className='summary-list'>
                  {summary.topPositives.slice(0, 3).map((positive, index) => (
                    <li key={index} className='summary-item positive'>
                      {positive}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {summary.topNegatives?.length > 0 && (
              <div className='summary-section negative'>
                <h5 className='summary-title'>
                  <Icons.ThumbsDown size={16} className='summary-icon' />
                  {t('ai_summary_common_concerns', 'Common Concerns')}
                </h5>
                <ul className='summary-list'>
                  {summary.topNegatives.slice(0, 2).map((negative, index) => (
                    <li key={index} className='summary-item negative'>
                      {negative}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderUseCases = () => {
    if (!aiSummary?.useCases?.length && !aiSummary?.targetAudience?.length) return null;
    return (
      <div className='ai-section use-cases'>
        <h4 className='section-title'>
          <Icons.Target size={20} className='section-icon' />
          {t('ai_summary_perfect_for', 'Perfect For')}
        </h4>
        <div className='use-cases-content'>
          {aiSummary.targetAudience?.length > 0 && (
            <div className='audience-tags'>
              <span className='label'>{t('ai_summary_target_audience', 'Target Audience')}:</span>
              <div className='tags'>
                {aiSummary.targetAudience.slice(0, 4).map((audience, index) => (
                  <span key={index} className='tag audience'>
                    {audience}
                  </span>
                ))}
              </div>
            </div>
          )}
          {aiSummary.useCases?.length > 0 && (
            <div className='use-case-tags'>
              <span className='label'>{t('ai_summary_use_cases', 'Use Cases')}:</span>
              <div className='tags'>
                {aiSummary.useCases.slice(0, 4).map((useCase, index) => (
                  <span key={index} className='tag use-case'>
                    {useCase}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCompatibility = () => {
    const attrs = aiSummary?.searchableAttributes;
    if (!attrs) return null;
    const hasCompatibility =
      attrs.compatibility?.length > 0 || attrs.availableSizes?.length > 0 || attrs.availableColors?.length > 0;
    if (!hasCompatibility) return null;
    return (
      <div className='ai-section compatibility'>
        <button
          className={`section-toggle ${expandedSection === 'compatibility' ? 'expanded' : ''}`}
          onClick={() => toggleSection('compatibility')}
        >
          <h4 className='section-title'>
            <Icons.Settings size={20} className='section-icon' />
            {t('ai_summary_compatibility', 'Compatibility & Options')}
          </h4>
          <Icons.ChevronDown
            size={16}
            className={`toggle-arrow ${expandedSection === 'compatibility' ? 'rotated' : ''}`}
          />
        </button>

        {expandedSection === 'compatibility' && (
          <div className='compatibility-content'>
            {attrs.compatibility?.length > 0 && (
              <div className='compat-item'>
                <span className='compat-label'>{t('ai_summary_compatible_with', 'Compatible with')}:</span>
                <div className='compat-tags'>
                  {attrs.compatibility.map((item, index) => (
                    <span key={index} className='compat-tag'>
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {attrs.availableSizes?.length > 0 && (
              <div className='compat-item'>
                <span className='compat-label'>{t('ai_summary_available_sizes', 'Available Sizes')}:</span>
                <div className='compat-tags'>
                  {attrs.availableSizes.slice(0, 8).map((size, index) => (
                    <span key={index} className='compat-tag size'>
                      {size}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {attrs.availableColors?.length > 0 && (
              <div className='compat-item'>
                <span className='compat-label'>{t('ai_summary_available_colors', 'Available Colors')}:</span>
                <div className='compat-tags'>
                  {attrs.availableColors.slice(0, 8).map((color, index) => (
                    <span key={index} className='compat-tag color'>
                      {color}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderCompactHeader = () => {
    const universalInsights = generateUniversalInsights;
    const topInsight = universalInsights[0];
    const confidence = aiSummary?.confidenceScore;

    const topPositive = reviewsAggregation?.summary?.topPositives?.[0];
    const topNegative = reviewsAggregation?.summary?.topNegatives?.[0];

    // Enhanced content for products without reviews
    const fallbackFeatures = generateFallbackFeatures;
    const hasEnoughReviewContent = topPositive || topNegative;

    return (
      <div className='ai-compact-header'>
        <div className='compact-info'>
          {hasRatingData && (
            <div className='compact-rating'>
              <div className='stars'>{renderStarRating(reviewsAggregation.averageRating)}</div>
              <span className='rating-value'>{reviewsAggregation.averageRating.toFixed(1)}</span>
              {hasReviewData && (
                <span className='reviews-count'>({reviewsAggregation.totalReviews.toLocaleString()})</span>
              )}
            </div>
          )}

          {hasConfidenceData && <div className='compact-confidence'>{renderConfidenceIndicator(confidence)}</div>}
        </div>

        <div className='compact-highlight'>
          <Icons.Sparkles size={14} className='highlight-icon' />
          <span className='highlight-text'>{topInsight}</span>
        </div>

        {/* Enhanced insights for products without sufficient review content */}
        {!hasEnoughReviewContent && universalInsights.length > 1 && (
          <div className='compact-insights'>
            {universalInsights.slice(1, 3).map((insight, index) => (
              <div key={index} className='compact-insight-item'>
                <Icons.Lightbulb size={12} className='insight-icon' />
                <span className='insight-text'>{insight}</span>
              </div>
            ))}
          </div>
        )}

        {/* Show fallback features for products without reviews */}
        {!hasReviewData && fallbackFeatures.length > 0 && (
          <div className='compact-features'>
            <div className='features-preview'>
              <span className='features-label'>{t('ai_summary_key_features', 'Key Features')}:</span>
              <div className='features-tags'>
                {fallbackFeatures.slice(0, 3).map((feature, index) => (
                  <span key={index} className='feature-tag'>
                    {feature}
                  </span>
                ))}
                {fallbackFeatures.length > 3 && <span className='more-features'>+{fallbackFeatures.length - 3}</span>}
              </div>
            </div>
          </div>
        )}

        {/* Review content for products with reviews */}
        {hasEnoughReviewContent && (
          <div className='compact-reviews'>
            {topPositive && (
              <div className='compact-review-item positive'>
                <Icons.ThumbsUp size={12} className='review-icon positive' />
                <span className='review-text'>{topPositive}</span>
              </div>
            )}
            {topNegative && (
              <div className='compact-review-item negative'>
                <Icons.ThumbsDown size={12} className='review-icon negative' />
                <span className='review-text'>{topNegative}</span>
              </div>
            )}
          </div>
        )}

        <div className='click-hint'>
          <span className='hint-text'>{t('ai_summary_click_to_expand', 'Click to see full AI insights')}</span>
        </div>
      </div>
    );
  };

  return (
    <div className='ai-summary-container'>
      <div
        className={`ai-summary-card ${isExpanded ? 'expanded' : 'collapsed'} ${!isExpanded ? 'clickable' : ''}`}
        onClick={
          !isExpanded
            ? e => {
                if (e.target.closest('button') || e.target.closest('a')) {
                  return;
                }
                toggleMainExpansion(e);
              }
            : undefined
        }
        role={!isExpanded ? 'button' : undefined}
        tabIndex={!isExpanded ? 0 : undefined}
        onKeyDown={
          !isExpanded
            ? e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleMainExpansion(e);
                }
              }
            : undefined
        }
      >
        <div className='ai-header'>
          <div className='ai-header-content'>
            <div className='ai-title-section'>
              <h3 className='ai-title'>
                <Icons.AIMagicStar size={20} className='ai-icon' />
                <span className='title-text'>{t('ai_summary_title', 'AI Insights')}</span>
              </h3>

              {/* Show More/Less Link - positioned next to title */}
              <span
                className='fw-500 cursor-pointer text-primary expand-text-link'
                role='button'
                tabIndex={0}
                onClick={e => {
                  e.stopPropagation();
                  toggleMainExpansion(e);
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleMainExpansion(e);
                  }
                }}
              >
                {isExpanded ? t('ai_summary_show_less', 'Show less') : t('ai_summary_show_more', 'Show more')}
              </span>
            </div>

            {/* AI Summary Button - when no AI summary exists */}
            {showAIButton && !aiSummary && likieeId && (
              <AISummaryButton
                likieeId={likieeId}
                className='ai-generate-button'
                onSuccess={onAIButtonSuccess}
                showCreditCost={true}
              />
            )}

            {/* AI Summary Regenerate Button - when AI summary exists */}
            {/* Temporarily hidden */}
            {/* {showAIButton && aiSummary && likieeId && (
              <AISummaryButton
                likieeId={likieeId}
                className='ai-regenerate-button compact mobile-compact'
                onSuccess={onAIButtonSuccess}
                isRegenerate={true}
                showCreditCost={true}
              />
            )} */}
          </div>

          {!isExpanded && renderCompactHeader()}

          {isExpanded && (
            <>
              {hasConfidenceData && renderConfidenceIndicator(aiSummary.confidenceScore)}
              <div className='ai-disclaimer'>
                <Icons.AlertTriangle size={16} className='disclaimer-icon' />
                <span className='disclaimer-text'>
                  {t('ai_summary_disclaimer', 'AI-generated insights may contain inaccuracies')}
                </span>
              </div>
            </>
          )}
        </div>

        {isExpanded && (
          <div className='ai-content'>
            {hasAIInsights && renderHighlights()}
            {hasReviewData && renderReviewsInsights()}
            {hasAIInsights && renderKeyFeatures()}
            {(aiSummary?.useCases?.length > 0 || aiSummary?.targetAudience?.length > 0) && renderUseCases()}
            {renderCompatibility()}
          </div>
        )}
      </div>
    </div>
  );
};

export default LikieeAISummary;
